import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import SearchIcon from "@mui/icons-material/Search";
import CustomButton from "../common/CustomButton";
import CustomSnackbar from "../common/CustomSnackbar";
import { mock_divisions } from "../../constData/MOCK_DATA_DRMT";
import BaseMainFilterAutocomplete from "./BaseMainFilterAutocomplete";

import "./TableFilters.sass";


function TableFilters({
  pageType,
  multipleEnabled = false,
  filterData,
  handleDivision,
  handleOriginCity,
  handleDestinationCity,
  handleFilterSearch,
}) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
    division: false,
    OCountry: false,
    DCountry: false,
  });

  const {
    masterData,
    globalError: errorMessage,
    isLoading,
  } = useSelector((state) => state.masterData);

  const [error, setError] = useState({
    open: false,
    duration: 3000,
    message: "",
  });

  useEffect(() => {
    if (errorMessage) {
      setError((prev) => ({ ...prev, open: true, message: errorMessage }));
    }
  }, [errorMessage]);

  const countryOptions = useMemo(
    () => [ ...(masterData?.countries?.data || [])],
    [masterData?.countries]
  );

  const divisionOptions = useMemo(
    () => masterData?.divisions?.data || [],
    [masterData?.divisions]
  );

  useEffect(() => {
    if (
      handleDivision &&
      divisionOptions.length > 0 &&
      (!filterData.division || filterData.division.length === 0)
    ) {
      const defaultDivision = {
        id: null,
        ...mock_divisions?.data[6],
      };

      const existsInOptions = divisionOptions.some(
        (option) => option?.divisionCode === defaultDivision.divisionCode
      );

      if (existsInOptions) {
        const actualDivision = divisionOptions.find(
          (option) => option.divisionCode === defaultDivision.divisionCode
        );
        handleDivision([actualDivision]);
      } else {
        handleDivision([defaultDivision]);
      }
    }
  }, [divisionOptions.length]);

  useEffect(() => {
    if (
      handleOriginCity &&
      handleDestinationCity &&
      countryOptions.length > 0 &&
      (!filterData.OCountry || (Array.isArray(filterData.OCountry) && filterData.OCountry.length === 0)) &&
      (!filterData.DCountry || (Array.isArray(filterData.DCountry) && filterData.DCountry.length === 0))
    ) {
      const northAmericaCountries = ["UNITED STATES", "CANADA", "MEXICO"];
      const northAmericaOptions = countryOptions.filter((country) =>
        northAmericaCountries.includes(country?.name)
      );

      if (northAmericaOptions.length === 3) {
        handleOriginCity(northAmericaOptions);
        handleDestinationCity(northAmericaOptions);
      }
    }
  }, [countryOptions.length, filterData.OCountry, filterData.DCountry, handleOriginCity, handleDestinationCity]);

  const handleSearch = () => {
    let newErrors = {};
    if (pageType === "bid_maintenance") {
      newErrors = {
        OCountry: !filterData.OCountry,
        DCountry: !filterData.DCountry,
      };
    } else if (pageType === "daily_maintenance") {
      newErrors = {
        division: !filterData.division || filterData.division.length === 0,
        OCountry: !filterData.OCountry,
        DCountry: !filterData.DCountry,
      };
    }

    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some(Boolean);
    if (hasErrors) return;
    handleFilterSearch();
  };

  return (
    <Stack sx={{ width: "100%", p: 0 }}>
      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {handleDivision &&
              <BaseMainFilterAutocomplete
                label = "Division"
                value = {filterData.division}
                error = {errors.division}
                onChange = {handleDivision}
                multiple = {true}
                options = {divisionOptions}
                accessor = "divisionCode"
                loading = {isLoading}
                helperText={errors.division ? t('select_division_error') : ""}
                disabled = {true}
              />}

            {handleOriginCity &&
              <BaseMainFilterAutocomplete
                  label="Origin Country"
                  value={filterData.OCountry}
                  onChange={handleOriginCity}
                  multiple = {multipleEnabled}
                  options={countryOptions}
                  accessor="name"
                  enableAllOption={true}
                  enableNorthAmerica={true}
                  enableNonNorthAmerica={true}
                  error={errors.OCountry}
                  helperText={errors.OCountry ? t('select_country_error') : ""}
                  loading={isLoading}
                />}
            {handleDestinationCity &&
              <BaseMainFilterAutocomplete
                label =  "Destination Country"
                value = {filterData.DCountry}
                error = {errors.DCountry}
                onChange = {handleDestinationCity}
                multiple = {multipleEnabled}
                options = {countryOptions}
                enableAllOption={true}
                enableNorthAmerica={true}
                enableNonNorthAmerica={true}
                accessor = "name"
                helperText={errors.DCountry ? t('select_destination_error') : ""}
                // errorMessage={t('select_destination_error')}
                loading = {isLoading}
              />}

            <Grid item xs={12} sm={6} md={3} className="dark-blue-btn-stack">
              <CustomButton
                fullWidth
                startIcon={<SearchIcon />}
                className="dark-blue-btn"
                onClick={handleSearch}
                datatestid="search"
                size="small"
                btnType="nonary"
              >
                Search
              </CustomButton>
            </Grid>
          </Grid>
          {error.open && (
            <CustomSnackbar
              open={error.open}
              severity="error"
              autoHideDuration={error.duration}
              datatestId="error-message"
              handleClose={() =>
                setError((prev) => ({
                  open: false,
                  message: "",
                  duration: 3000,
                }))
              }
              message={errorMessage}
            />
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

export default TableFilters;